<template>
  <div
    class="absolute top-0 left-0 flex flex-col w-full h-full bg-white border shadow-sm"
  >
    <header
      class="flex items-center justify-between w-full p-4 bg-white border-b md:p-5 rounded-t-2xl"
    >
      <div class="flex items-center gap-3">
        <button
          class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all rounded-lg hover:bg-slate-50 text-eblack-800 hover:text-eblack-900"
          @click="openDatePicker"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>{{ getCurrentDateFormatted() }}</span>
        </button>
        <input
          type="date"
          ref="dateInput"
          @change="handleDateChange"
          class="absolute w-0 h-0 opacity-0"
        />
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="onSubtractOneDay"
          class="flex items-center justify-center transition-all border rounded-lg h-9 w-9 hover:bg-slate-50"
          type="button"
          :aria-label="$t('calendar.auto.dia_anterior')"
        >
          <svg
            class="text-eorange-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m14 8-4 4 4 4"
            />
          </svg>
        </button>
        <button
          @click="onToday"
          class="px-4 py-2 text-sm font-medium transition-all rounded-lg bg-eorange-50 text-eorange-600 hover:bg-eorange-100"
          type="button"
        >{{ $t("calendar.auto.hoy") }}</button>
        <button
          @click="onAddOneDay"
          class="flex items-center justify-center transition-all border rounded-lg h-9 w-9 hover:bg-slate-50"
          type="button"
          :aria-label="$t('calendar.auto.dia_siguiente')"
        >
          <svg
            class="text-eorange-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m10 16 4-4-4-4"
            />
          </svg>
        </button>
      </div>
    </header>
    <article class="h-full p-2 overflow-x-hidden overflow-y-auto">
      <GanttChart
        :chartStart="chartStart"
        :chartEnd="chartEnd"
        :rows="calendarRows"
        @select-service="openServiceDrawer"
        @select-bar="openBookingDetailsModal"
      />
    </article>
    <footer
      class="flex items-center justify-center w-full p-4 bg-white border-t md:p-5 rounded-b-2xl"
    >
      <button
        v-if="canManageServices"
        class="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-eorange-400 hover:bg-eorange-500 text-white font-medium text-sm transition-all hover:shadow-md"
        @click="openServiceForm"
      >
        <svg
          class="text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 12h14m-7 7V5"
          />
        </svg>
        <span>{{ $t("calendar.auto.agregar_nuevo_servicio") }}</span>
      </button>
    </footer>
  </div>
</template>

<script setup>
import { i18n } from "@/i18n";
import { ref, computed, watch, onMounted } from "vue";
import { initFlowbite } from "flowbite";
import { useToast } from "vue-toastification";
import BookingDetailsModal from "./components/BookingDetailsModal.vue";
import BookingDetailDrawer from "@/domains/reservations/components/BookingDetailDrawer.vue";
import ServiceFormSidebar from "../Services/components/ServiceFormSidebar.vue";
import GanttChart from "./components/GanttChart.vue";
import { useServiceStore } from "@domains/service/stores/service.store";
import { calendarRepository } from "@domains/scheduling/repositories/calendar.repository";
import { useConversationCreation } from "@domains/conversation/composables/useConversationCreation";
import { useOverlay } from "@/services/overlay-service";
import { createModalConfig, createSidebarConfig } from "@/utils/overlay-utils";
import { generateAllBarsForService } from "@domains/scheduling/utils/gantt-bar-generator";
import { useAbility } from "@/domains/auth";

const toast = useToast();
const serviceStore = useServiceStore();
const overlayService = useOverlay();
const { can } = useAbility();
const { findOrRedirect } = useConversationCreation();
const dateInput = ref(null);

const _now = new Date();
const currentDate = ref(
  new Date(_now.getFullYear(), _now.getMonth(), _now.getDate()),
);
const slots = ref([]);
const calendarRows = ref([]);
const detailedSlots = ref(new Map()); // Map<serviceId, DetailedSlot[]>
const schedules = ref(new Map()); // Map<serviceId, ScheduleResponse['schedule']>
const serviceDurations = ref(new Map()); // Map<serviceId, number> - duration in minutes
const canManageServices = computed(() => can("manage", "Service") || can("manage", "all"));

const chartStart = computed(() => {
  const dateStr = formatDateOnly(currentDate.value);
  return `${dateStr} 00:00`; // Midnight Costa Rica time
});

const chartEnd = computed(() => {
  const date = new Date(currentDate.value);
  date.setDate(date.getDate() + 1); // Next day
  const dateStr = formatDateOnly(date);
  return `${dateStr} 00:00`; // Midnight next day Costa Rica time
});

watch(currentDate, async () => {
  // Clear existing data
  slots.value = [];
  detailedSlots.value.clear();
  schedules.value.clear();
  serviceDurations.value.clear();

  // Only refetch slots for each service (services already loaded)
  const services = serviceStore.services;
  if (services.length > 0) {
    await Promise.all(services.map((service) => getSlots(service)));
  }
});

async function initData() {
  await serviceStore.fetchServices(true);

  const services = serviceStore.services;
  for (const service of services) {
    getSlots(service);
  }
}

async function getSlots(service) {
  try {
    const dateStr = formatDate(new Date(currentDate.value)).split("T")[0];

    // Fetch both slots and schedule in parallel
    const [slotsResponse, scheduleResponse] = await Promise.all([
      calendarRepository.getSlotsByDate(service.id, dateStr, "detailed"),
      calendarRepository
        .getScheduleByDate(service.id, dateStr)
        .catch(() => null),
    ]);

    // Store schedule for bar generation
    if (scheduleResponse?.schedule) {
      schedules.value.set(service.id, scheduleResponse.schedule);
    }

    // Store service duration from slots response
    if (slotsResponse.service_duration_minutes) {
      serviceDurations.value.set(
        service.id,
        slotsResponse.service_duration_minutes,
      );
    }

    if (slotsResponse.slots && Array.isArray(slotsResponse.slots)) {
      // Store detailed slots for later use in modal
      detailedSlots.value.set(service.id, slotsResponse.slots);

      const slotsWithServiceId = slotsResponse.slots.map((slot) => ({
        ...slot,
        serviceId: service.id,
        // Pre-calculate display values
        displayLabel: formatSlotLabel(slot),
        utilization: calculateUtilization(slot),
      }));
      slots.value = [...slots.value, ...slotsWithServiceId];
    }
  } catch (error) {
    console.error(`Error fetching data for service ${service.id}:`, error);
  } finally {
    calculateRowData();
  }
}

function formatSlotLabel(slot) {
  // For detailed slots, show booking count and capacity
  const bookings = slot.summary?.total_bookings || slot.bookings?.length || 0;
  const participants =
    slot.summary?.total_participants || slot.quantity_of_participants || 0;
  const total = slot.summary?.capacity?.total || slot.capacity?.total || 0;

  if (bookings > 0) {
    return `${bookings} reserva${
      bookings !== 1 ? "s" : ""
    } (${participants}/${total})`;
  }
  // Fallback for non-detailed view
  return participants > 0 ? `${participants}/${total}` : "";
}

function calculateUtilization(slot) {
  const booked =
    slot.summary?.total_participants || slot.quantity_of_participants || 0;
  const total = slot.summary?.capacity?.total || slot.capacity?.total || 1;
  return Math.round((booked / total) * 100);
}

function calculateRowData() {
  calendarRows.value = serviceStore.services.map((service) => {
    const serviceSlots = detailedSlots.value.get(service.id) || [];
    const serviceSchedule = schedules.value.get(service.id) || null;
    const durationMinutes = serviceDurations.value.get(service.id);

    // Use new comprehensive bar generator
    const bars = generateAllBarsForService(
      service,
      serviceSchedule,
      serviceSlots,
      currentDate.value,
      durationMinutes,
    );

    return {
      id: service.id,
      label: service.name,
      bars,
    };
  });
}

function getBarColor(utilization) {
  if (utilization >= 100) return "#ef4444"; // Red - full
  if (utilization >= 75) return "#f97316"; // Orange - high
  if (utilization > 0) return "#eab308"; // Yellow - partial
  return "#22c55e"; // Green - empty
}

function getCurrentDateFormatted() {
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  let date = new Date(currentDate.value)
    .toLocaleDateString("es-CR", options)
    .replace(" de ", " del ")
    .replace(/(\d+) del (\w+) del (\d{4})/, "$1 de $2 del $3");
  return date.charAt(0).toUpperCase() + date.slice(1);
}

function onAddOneDay() {
  const nextDay = new Date(currentDate.value);
  nextDay.setDate(nextDay.getDate() + 1);
  currentDate.value = nextDay;
}

function onSubtractOneDay() {
  const previousDay = new Date(currentDate.value);
  previousDay.setDate(previousDay.getDate() - 1);
  currentDate.value = previousDay;
}

function onToday() {
  const now = new Date();
  currentDate.value = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
}

function openDatePicker() {
  const input = dateInput.value;
  if (input?.showPicker) {
    input.showPicker();
  } else {
    input?.click();
  }
}

function handleDateChange(event) {
  const [year, month, day] = event.target.value.split("-").map(Number);
  currentDate.value = new Date(year, month - 1, day);
}

function formatDateTime(dateObj) {
  const pad = (n) => n.toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  const month = pad(dateObj.getMonth() + 1);
  const day = pad(dateObj.getDate());
  const hours = pad(dateObj.getHours());
  const minutes = pad(dateObj.getMinutes());
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function openServiceDrawer(row) {
  const serviceId = parseInt(row.id);
  const fullService = serviceStore.services.find((s) => s.id === serviceId);

  if (!fullService) {
    toast.error(i18n.global.t("calendar.auto.servicio_no_encontrado"));
    return;
  }

  overlayService.open(
    createSidebarConfig({
      component: ServiceFormSidebar,
      props: {
        service: fullService,
        showDetails: !canManageServices.value,
        readOnly: !canManageServices.value,
        loadServices: initData,
        overlayService: overlayService,
      },
      onClose: () => {
        initData(); // Refresh calendar data
      },
    }),
  );
}

function openBookingDetailsModal(payload) {
  const { bar, row } = payload;
  const metadata = bar.ganttBarConfig?.metadata;

  if (!metadata) {
    toast.warning(i18n.global.t("calendar.auto.no_hay_datos_disponibles"));
    return;
  }

  // Handle different bar types
  switch (metadata.type) {
    case "unavailable":
      toast.info(
        metadata.reason || "Este horario no está disponible para reservas",
      );
      break;

    case "available-empty":
      toast.info(i18n.global.t("calendar.auto.este_horario_esta_disponible_pero_no_tiene_reser"));
      break;

    case "booked":
      openBookingModal(bar, row);
      break;

    default:
      toast.warning(i18n.global.t("calendar.auto.tipo_de_horario_desconocido"));
  }
}

function openBookingModal(bar, row) {
  const slotData = bar.ganttBarConfig?.slotData;

  if (!slotData) {
    toast.warning(i18n.global.t("calendar.auto.no_hay_datos_de_reservas_para_este_horario"));
    return;
  }

  const service = serviceStore.services.find((s) => s.id === parseInt(row.id));
  const serviceName = service?.name || row.label;

  // Format time range
  const startTime = slotData.start_time?.slice(0, 5) || "";
  const endTime = slotData.end_time?.slice(0, 5) || "";
  const timeRange = `${startTime} - ${endTime}`;

  // Get bookings - from detailed view they're in slot.bookings
  const bookings = slotData.bookings || [];

  // Get summary - from detailed view it's in slot.summary
  const summary = slotData.summary || {
    total_bookings: bookings.length,
    total_participants: slotData.quantity_of_participants || 0,
    capacity: slotData.capacity || { total: 0, available: 0 },
  };

  overlayService.open(
    createModalConfig({
      component: BookingDetailsModal,
      size: "lg",
      position: "center",
      props: {
        serviceName,
        timeRange,
        formattedDate: getCurrentDateFormatted(),
        bookings,
        summary,
      },
      events: {
        "start-chat": (booking) => handleStartBookingChat(booking, slotData, row),
        "view-booking": (booking) => handleViewBookingDetails(booking, slotData),
      },
    }),
  );
}

async function handleStartBookingChat(booking, slotData, row) {
  const serviceId = String(
    booking.service?.id ?? slotData.service_id ?? row.id ?? "",
  );
  const itineraryId = String(booking.itinerary_id ?? "");
  const bookingId = String(booking.booking_id ?? booking.id ?? "");
  const slotId = String(booking.slot_id ?? slotData.id ?? "");

  if (!serviceId || !itineraryId || !bookingId) {
    toast.error(i18n.global.t("calendar.auto.no_se_pudo_iniciar_el_chat_faltan_datos_de_la_re"));
    return;
  }

  try {
    await findOrRedirect({
      serviceId,
      itineraryId,
      bookingId,
      ...(slotId ? { slotId } : {}),
    });
    overlayService.closeTop(true);
  } catch (error) {
    console.error("[CalendarIndex] Error starting booking chat:", error);
    toast.error(i18n.global.t("calendar.auto.no_se_pudo_iniciar_el_chat_de_la_reserva"));
  }
}

function resolveBookingAmount(booking) {
  return (
    booking?.provider_facing_amount ??
    booking?.total_amount ??
    booking?.subtotal ??
    booking?.pricing?.total_amount ??
    null
  );
}

function splitCustomerName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { name: parts[0] || "Cliente", last_name: "" };
  }

  return {
    name: parts.slice(0, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
}

function buildProviderBookingDetail(booking, slotData) {
  const itineraryId = Number(booking.itinerary_id);
  const bookingId = Number(booking.booking_id ?? booking.id);
  const serviceId = Number(booking.service?.id ?? slotData.service_id ?? 0);
  const amount = resolveBookingAmount(booking);
  const customerName = splitCustomerName(booking.customer?.name);

  return {
    id: itineraryId,
    code: booking.itinerary_code || `IT-${itineraryId || bookingId}`,
    state: booking.itinerary_state,
    customer: booking.customer
      ? {
          id: booking.customer.id,
          name: customerName.name,
          last_name: customerName.last_name,
          whatsapp_phone: booking.customer.phone,
        }
      : null,
    quantity_of_participants: booking.participants ?? 0,
    slots_itineraries: [
      {
        id: bookingId,
        booking_id: bookingId,
        service_id: serviceId,
        service_slot_id: booking.service_slot_id ?? slotData.id ?? null,
        quantity_of_participants: booking.participants ?? 0,
        total_amount: amount,
        provider_facing_amount: amount,
        service: booking.service
          ? {
              id: serviceId,
              name: booking.service.name,
            }
          : null,
        service_slot: {
          id: booking.service_slot_id ?? slotData.id ?? null,
          start_date: slotData.start_date,
          end_date: slotData.end_date,
        },
        fulfillment_status: booking.fulfillment?.status ?? null,
        fulfillment_notes: booking.fulfillment?.notes ?? null,
      },
    ],
  };
}

function handleViewBookingDetails(booking, slotData) {
  const itineraryId = Number(booking.itinerary_id);
  const bookingId = String(booking.booking_id ?? booking.id ?? "");

  if (!Number.isFinite(itineraryId) || !bookingId) {
    toast.error(i18n.global.t("calendar.auto.no_se_pudo_abrir_el_detalle_faltan_datos_de_la_r"));
    return;
  }

  try {
    overlayService.open(
      createSidebarConfig({
        component: BookingDetailDrawer,
        props: {
          booking: buildProviderBookingDetail(booking, slotData),
        },
      }),
    );
  } catch (error) {
    console.error("[CalendarIndex] Error opening booking details:", error);
    toast.error(i18n.global.t("calendar.auto.no_se_pudo_abrir_el_detalle_de_la_reserva"));
  }
}

function formatDate(date) {
  const pad = (n) => (n < 10 ? "0" + n : n);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDateOnly(date) {
  const pad = (n) => (n < 10 ? "0" + n : n);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

function openServiceForm() {
  overlayService.open(
    createSidebarConfig({
      component: ServiceFormSidebar,
      props: {
        service: null,
        showDetails: false,
        readOnly: false,
        loadServices: initData,
        overlayService: overlayService,
      },
      onClose: () => {
        initData();
      },
    }),
  );
}

onMounted(() => {
  initFlowbite();
  initData();
});
</script>
